import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { useGetCreatorDiscoverySettingsQuery } from '@frontend/app/hooks/creatorDiscovery/useGetCreatorDiscoverySettings';
import { useGetCurrentClient } from '@frontend/app/hooks';
import { useAuth } from '@frontend/context/authContext';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@frontend/shadcn/components/ui/sheet';
import { Tabs, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { SlidersIcon } from '@revfluence/fresh-icons/regular/esm';
import { Spinner } from '../Analytics/components/ui/spinner';
import {
  Header,
  HeaderActions,
  HeaderH1,
  HeaderLayout,
  HeaderMainContent,
  HeaderTabs,
  HeaderTabsList,
  HeaderTabsTrigger,
} from '../components/header';
import CreatorSearchBadges from './components/badges';
import useSearchBadges from './components/badges/hooks/use-search-badges';
import { EmailResourceDropdown } from './components/email-resource-dropdown';
import { FiltersForm } from './components/filters-form';
import { InstagramAccountDropdown } from './components/instagram-account-dropdown';
import { SearchForm } from './components/search-form';
import { useCreatorSearchParams } from './hooks/use-creator-search-params';
import {
  castSearchQueryFormDataToQueryVariables,
  SearchQueryFormData,
  searchQueryFormSchema,
  useSearchQueryForm,
} from './hooks/use-filters-form';
import { usePagination } from './hooks/use-pagination';
import { useSelectedEmailResource } from './hooks/use-selected-email-resource';
import { useSelectedInstagramAccount } from './hooks/use-selected-instagram-account';
import { defaultInstagramColumns } from './instagram/components/table/config/default-instagram-columns';
import { useInstagramCreatorSearchData } from './instagram/hooks/use-instagram-creator-search-data';
import { useInstagramCreatorSearchTab } from './instagram/hooks/use-instagram-creator-search-tab';
import { InstagramCreatorSearchTab } from './instagram/instagram-creator-search-tab';
import { buildInstagramDefaultFiltersPrompt } from './instagram/utils/build-instagram-default-filters-prompt';
import { useLoadingStates } from './hooks/use-loading-states';
import { QueryVariables } from './types';

const DEFAULT_VARIABLES: QueryVariables = {
  before: null,
  after: null,
  limit: 100,
  query: '',
  prompt: '',
  usernames: [],
  creatorUsername: '',
  ageBuckets: [],
  genders: [],
  countries: [],
  interests: [],
  minFollowers: null,
  maxFollowers: null,
  minEngagements: null,
  maxEngagements: null,
  majorAudienceAgeBuckets: [],
  majorAudienceGenders: [],
  majorAudienceCountries: [],
  similarToCreators: null,
  requireInCreatorMarketplace: null,
  hasEmail: null,
};

export const CreatorSearch: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const { clientInfo } = useAuth();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(defaultInstagramColumns);

  const clientId = clientInfo?.id;

  // Get current client information for personalized prompts
  const { client: currentClient } = useGetCurrentClient();

  // Call the hook here
  const {
    data: creatorDiscoverySettings,
    error: creatorDiscoverySettingsError,
    loading: creatorDiscoverySettingsLoading,
  } = useGetCreatorDiscoverySettingsQuery();

  // Use the creator search hook
  const {
    queryVariables,
    handleClearAll,
    updateUrlParams,
    hasSearchFilters,
    defaultFiltersResolved,
    generateFilter,
    isGeneratingFilter,
    initialAiGenerationMade,
  } = useCreatorSearchParams({
    defaultVariables: DEFAULT_VARIABLES,
  });

  // Build personalized prompt based on client information
  const buildInitialPrompt = useCallback(() => {
    return buildInstagramDefaultFiltersPrompt(currentClient);
  }, [currentClient]);

  useEffect(() => {
    if (creatorDiscoverySettingsError && !creatorDiscoverySettingsLoading) {
      console.error('Error loading Creator Discovery Settings:', creatorDiscoverySettingsError);
    }
  }, [creatorDiscoverySettingsError, creatorDiscoverySettingsLoading]);

  // Use the new search query form hook
  const {
    form,
    submitSearchQueryForm,
    setValue: setFormValue,
  } = useSearchQueryForm({
    defaultValues: queryVariables,
    onSubmit: (data: SearchQueryFormData) => {
      // Update query variables with form data
      updateUrlParams({
        ...DEFAULT_VARIABLES,
        query: data.query || '',
      });
      handleSearch();
    },
  });

  // Sync form with queryVariables when they change (e.g., after AI suggestions)
  useEffect(() => {
    if (defaultFiltersResolved) {
      form.reset(queryVariables);
    }
  }, [queryVariables, defaultFiltersResolved, form]);

  const { emailResources, instagramAccounts } = useMemo(
    () => ({
      emailResources: creatorDiscoverySettings?.email?.resources || [],
      instagramAccounts: creatorDiscoverySettings?.instagram?.accounts || [],
    }),
    [creatorDiscoverySettings],
  );

  const { selectedEmailResource, setSelectedEmailResource } = useSelectedEmailResource({ emailResources });
  const { selectedInstagramAccount, setSelectedInstagramAccount } = useSelectedInstagramAccount({ instagramAccounts });

  const {
    fetching,
    error,
    handleSearch,
    handlePagination,
    isPaginationFetching,
    searchResults,
  } = useInstagramCreatorSearchData(queryVariables, clientId, selectedInstagramAccount, defaultFiltersResolved);

  // Use the new loading states hook to centralize all loading logic
  const loadingStates = useLoadingStates({
    creatorDiscoverySettingsLoading,
    defaultFiltersResolved,
    fetching,
    isGeneratingFilter,
    isPaginationFetching,
    hasSearchResults: (searchResults?.discoverInstagramCreators?.creators?.length ?? 0) > 0,
    hasSearchFilters,
  });

  // Extract UI states from the hook (replacing old inline logic)
  const { shouldShowMainSpinner, shouldShowRefreshSpinner, shouldDisableInteractions } = loadingStates.uiStates;

  // Legacy compatibility - keep for components that haven't been updated yet
  const isLoading = shouldShowMainSpinner;

  const { badgesProps } = useSearchBadges({
    queryVariables,
    form,
    handleClearAll,
    handleSearch,
    setIsFiltersOpen,
    updateUrlParams,
    loadingStates,
    generateFilter,
    buildInitialPrompt,
  });

  const { onPageChange } = usePagination({
    queryVariables,
    updateUrlParams,
    handlePagination,
  });

  const { creatorInstagramCreatorSearchTabProps } = useInstagramCreatorSearchTab({
    data: searchResults,
    clientId,
    queryVariables,
    setSelectedColumns,
    selectedColumns,
    onPageChange,
    emailResource: selectedEmailResource,
    instagramAccount: selectedInstagramAccount,
    handleClearAllFilters: handleClearAll,
    hasSearchFilters,
    loadingStates,
    updateUrlParams,
    handleSearch,
  });

  // Simulate refresh functionality
  const handleRefresh = useCallback(() => {
    handleSearch();
  }, [handleSearch]);

  // Handle tab navigation
  const handleTabChange = (value: string) => {
    console.log('handleTabChange: ', value);
  };

  const onFiltersOpenChange = (open: boolean) => {
    // if closing, reset form to query variables
    if (!open) {
      form.reset(queryVariables);
    }
    setIsFiltersOpen(open);
  };

  const handleCancelFilters = () => {
    form.reset(queryVariables);

    setIsFiltersOpen(false);
  };

  // Update to use the original filters form data
  const handleApplyFilters = (formData: z.infer<typeof searchQueryFormSchema>) => {
    const typedData = castSearchQueryFormDataToQueryVariables(formData);
    updateUrlParams(typedData);
  };

  const handleApplyFiltersAndClose = (formData: z.infer<typeof searchQueryFormSchema>) => {
    handleApplyFilters(formData);
    setIsFiltersOpen(false);
  };

  // Update search input handler to work with the form
  const handleFormSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormValue('prompt', value);
  };

  // Submit search form
  const handleSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    submitSearchQueryForm();
  };

  // Handle search submissions
  const handleSearchSubmit = (query: string) => {
    // use generateFilter to get AI-suggested filters
    const trimmedQuery = query.trim();

    // Only update URL if user entered a non-empty prompt
    // Empty user prompts should generate filters but not be in URL
    if (trimmedQuery !== '') {
      updateUrlParams({
        ...queryVariables,
        prompt: trimmedQuery,
      });
    }

    // Generate filters with user input (even if empty)
    // If empty, only generate if we haven't already made an initial AI generation call
    // to prevent redundant requests with the same default prompt
    if (trimmedQuery !== '' || !initialAiGenerationMade) {
      generateFilter(trimmedQuery !== '' ? trimmedQuery : buildInitialPrompt(), trimmedQuery !== ''); // Mark as user-entered only if non-empty
    }
  };

  const headerActions = (
    <>
      {(tab === 'instagram' || !tab) && (
        <InstagramAccountDropdown
          instagramAccounts={instagramAccounts}
          selectedInstagramAccount={selectedInstagramAccount}
          setSelectedInstagramAccount={setSelectedInstagramAccount}
          isLoading={creatorDiscoverySettingsLoading}
        />
      )}
      <EmailResourceDropdown
        emailResources={emailResources}
        selectedEmailResource={selectedEmailResource}
        setSelectedEmailResource={setSelectedEmailResource}
        isLoading={creatorDiscoverySettingsLoading}
      />
      <Button
        size="headerIcon"
        variant="outlineHeader"
        disabled={shouldDisableInteractions}
        onClick={handleRefresh}
        data-testid="refresh-button"
        aria-label={shouldShowRefreshSpinner ? 'Refreshing data' : 'Refresh data'}
        aria-busy={shouldShowRefreshSpinner}
        className="flex items-center justify-center"
      >
        <Spinner size="sm" spinning={shouldShowRefreshSpinner} />
      </Button>
      <Sheet open={isFiltersOpen} onOpenChange={onFiltersOpenChange}>
        <SheetTrigger asChild>
          <Button size="headerIcon" variant="outlineHeader" aria-label="Open filters" aria-expanded={isFiltersOpen}>
            <SlidersIcon aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent className="py-6 px-0">
          <SheetHeader className="flex justify-start gap-2 px-6 mt-auto border-b bg-background">
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription className="sr-only">
              Apply filters to refine creator search results. Use the form below to set your criteria.
            </SheetDescription>
          </SheetHeader>
          <FiltersForm form={form} onSubmit={handleApplyFiltersAndClose} onCancel={handleCancelFilters} />
        </SheetContent>
      </Sheet>
    </>
  );

  const headerTabs = (
    <HeaderTabsList role="tablist" aria-label="Creator search platforms">
      <HeaderTabsTrigger
        value="instagram"
        data-action="instagram-tab-click"
        data-dd-action-name="instagram-tab-click"
        aria-controls="instagram-tab"
        role="tab"
        aria-current={tab === 'instagram' ? 'page' : undefined}
      >
        Instagram
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="tiktok"
        data-action="tiktok-tab-click"
        data-dd-action-name="tiktok-tab-click"
        aria-controls="tiktok-tab"
        role="tab"
        aria-current={tab === 'tiktok' ? 'page' : undefined}
        className="hidden"
      >
        TikTok
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="youtube"
        data-action="youtube-tab-click"
        data-dd-action-name="youtube-tab-click"
        aria-controls="youtube-tab"
        role="tab"
        aria-current={tab === 'youtube' ? 'page' : undefined}
        className="hidden"
      >
        YouTube
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="pinterest"
        data-action="pinterest-tab-click"
        data-dd-action-name="pinterest-tab-click"
        aria-controls="pinterest-tab"
        role="tab"
        aria-current={tab === 'pinterest' ? 'page' : undefined}
        className="hidden"
      >
        Pinterest
      </HeaderTabsTrigger>
    </HeaderTabsList>
  );

  return (
    <div className="flex h-full flex-col">
      <TooltipProvider>
        <Tabs value={tab || 'instagram'} onValueChange={handleTabChange}>
          <Header>
            <HeaderLayout>
              <HeaderMainContent>
                <HeaderH1>Creator Search</HeaderH1>
              </HeaderMainContent>
              <HeaderActions className="space-x-2 p-4 pb-1 sm:space-x-4 sm:p-4">{headerActions}</HeaderActions>
            </HeaderLayout>
            <HeaderTabs>{headerTabs}</HeaderTabs>
          </Header>

          <div className="mt-4">
            <div className="px-4">
              <div className="flex items-center">
                <h2 className="text-lg font-semibold">Search for Creators</h2>
              </div>
              <div className="my-4 relative">
                <SearchForm
                  form={form}
                  onSubmit={handleSubmitSearch}
                  onSearchChange={handleFormSearchChange}
                  onSearchSubmit={handleSearchSubmit}
                  fetching={isLoading}
                  loadingStates={loadingStates}
                />
              </div>
              {/* Search Results Display */}
              {error && (
                <div className="mt-6 border rounded-lg p-4 bg-red-50 text-red-600">
                  <h3 className="text-md font-medium mb-2">Error</h3>
                  <p>{error.message || 'An error occurred while searching. Please try again.'}</p>
                </div>
              )}
            </div>
            <div className="mt-4">
              <div className="px-4">
                <CreatorSearchBadges {...badgesProps} />
              </div>
            </div>
            <TabsContent value="instagram" id="instagram-tab" role="tabpanel" aria-live="polite">
              <InstagramCreatorSearchTab {...creatorInstagramCreatorSearchTabProps} />
            </TabsContent>
            <TabsContent value="tiktok" id="tiktok-tab" role="tabpanel" aria-live="polite">
              <div className="px-4">
                <p className="text-center py-12 text-muted-foreground">TikTok creator search coming soon</p>
              </div>
            </TabsContent>
            <TabsContent value="youtube" id="youtube-tab" role="tabpanel" aria-live="polite">
              <div className="px-4">
                <p className="text-center py-12 text-muted-foreground">YouTube creator search coming soon</p>
              </div>
            </TabsContent>
            <TabsContent value="pinterest" id="pinterest-tab" role="tabpanel" aria-live="polite">
              <div className="px-4">
                <p className="text-center py-12 text-muted-foreground">Pinterest creator search coming soon</p>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Status announcer for screen readers */}
      <output aria-live="polite" className="sr-only">
        {isGeneratingFilter && 'Generating AI suggestions...'}
        {fetching && !isGeneratingFilter && 'Searching for creators...'}
        {isPaginationFetching && 'Loading more results...'}
      </output>
    </div>
  );
};
